import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import DialogWindow from '@/common/components/_misc/DialogWindow';
import {
  Warning as WarningIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  Update as UpdateIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';

interface SyncConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  versionNumber: number;
  newerFiles: number;
  behindFiles: number;
  deletedFiles: number;
  newFiles: number;
  isSyncing?: boolean;
}

const SyncConfirmDialog: React.FC<SyncConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  versionNumber,
  newerFiles,
  behindFiles,
  deletedFiles,
  newFiles,
  isSyncing = false,
}) => {
  const discardsLocalFiles = newerFiles + behindFiles + newFiles > 0;
  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      title={`Sync to Version ${versionNumber}`}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'warning.main',
        }}
      >
        <WarningIcon />
        <Typography variant="body2" color="warning.main">
          Warning: This will overwrite local changes
        </Typography>
      </Box>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            You have {newerFiles} local files that are newer than Version{' '}
            {versionNumber}.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Syncing will make your local project match Version {versionNumber}.
            Files that differ will be replaced, and local-only files will be
            deleted.
          </Typography>
          {discardsLocalFiles && (
            <Alert severity="warning">
              <Typography variant="body2">
                Any local changes you haven’t uploaded will be permanently lost
                when those files are overwritten or deleted. Cancel and create a
                new version first if you want to keep them.
              </Typography>
            </Alert>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Changes breakdown:
        </Typography>
        <List dense sx={{ py: 0 }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SyncIcon color="warning" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`${newerFiles} newer local files (will be overwritten)`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <UpdateIcon color="info" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`${behindFiles} outdated local files (will be updated)`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DownloadIcon color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`${deletedFiles} missing local files (will be downloaded)`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DeleteIcon color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`${newFiles} local-only files (will be deleted)`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Beta Feature:</strong> This sync functionality is still in
            beta. Please keep backups of your important files before proceeding.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" disabled={isSyncing}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          disabled={isSyncing}
          startIcon={
            isSyncing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <WarningIcon />
            )
          }
        >
          {isSyncing ? 'Syncing...' : 'Continue and Lose Local Changes'}
        </Button>
      </DialogActions>
    </DialogWindow>
  );
};

export default SyncConfirmDialog;
